import React from 'react'
import { Label, Loader, Segment, Button } from 'semantic-ui-react'
import styled from 'styled-components'

import { formatNumber } from 'organisms/Billing/utils'
import { priceAmount, priceInterval } from 'libs/billing/price'
import { timestampFormat } from 'libs/date-format'
import { AllowPermission } from 'shared/components/AllowPermission'
import constants from 'config/constants'
import usePermission from 'hooks/usePermission'

/**
 *
 * @typedef Subscription
 * @property {string} status
 * @property {string} [trial_end] e.g. "2021-02-17T15:59:23Z"
 */

/**
 *
 * @typedef Plan
 * @property {string} nickname
 * @property {number} unit_amount in minor units, e.g. 9900 for $99.00
 * @property {string} currency
 * @property {{ interval?: string, interval_count?: number }} recurring
 */

/**
 *
 * @typedef Usage
 * @property {number} usage
 * @property {string} limit
 * @property {object} [overage]
 * @property {number} overage.charge
 * @property {string} overage.currency
 */

/**
 *
 * @typedef Coupon
 * @property {string} name
 * @property {string} [amount_off] e.g. "20.00" — dollars, not Stripe cents
 * @property {string} [percent_off] e.g. "50.00"
 * @property {string} [currency]
 */
// Coupon expiry lives on the PARENT (subscription.coupon_end,
// billing.stripe_customer.coupon_end) — not on the coupon itself.
// The expiry-guard lives in libs/billing/get-active-coupon.

/**
 *
 * @param {object} props
 * @param {Subscription} props.subscription
 * @param {Plan} props.plan
 * @param {Coupon} [props.coupon]
 * @param {React.ReactNode} [props.failedPaymentMessageElement]
 * @param {Function} props.onModalOpened
 * @param {string} props.cancellationLink
 * @param {Date} props.dueDate
 */
export default function AccountPlanAndUsage({
  subscription,
  plan,
  dueDate,
  coupon,
  failedPaymentMessageElement,
  onModalOpened,
  cancellationLink,
  nextInvoiceAmount = null,
  isNextInvoiceAmountLoading = false,
}) {
  const { hasPermission } = usePermission()
  const hasManageBillingPermission = hasPermission(constants.permissions.manageBillingInformation)

  if (!subscription || ['canceled', 'incomplete_expired'].includes(subscription.status)) {
    const inactiveLabel = subscription?.status === 'incomplete_expired' ? 'Expired' : 'Canceled'
    return (
      <Segment>
        <UsageSection
          onModalOpened={onModalOpened}
          title="Plan"
          detail={<span>{inactiveLabel}</span>}
          showPlanButton={hasManageBillingPermission}
        />
      </Segment>
    )
  }

  if (subscription.status === 'trialing') {
    return (
      <Segment>
        <UsageSection
          onModalOpened={onModalOpened}
          title="Plan"
          detail={[
            <span key="1">Free trial</span>,
            <em key="2" className="text-grey">
              Your trial will end on {timestampFormat(subscription.trial_end, 'mmm dd, yyyy')}
            </em>,
          ]}
          showPlanButton={hasManageBillingPermission}
        />
      </Segment>
    )
  }

  return (
    <React.Fragment>
      <Segment.Group>
        <Segment>
          <UsageSection
            title="Plan"
            detail={<Plan plan={plan} subscription={subscription} />}
            priceDetail={renderPlanPricing(subscription, plan)}
          />
          {subscription.status === 'past_due' && failedPaymentMessageElement}
          {coupon && (
            <UsageSection
              title="Discount"
              detail={coupon.name}
              priceDetail={renderDiscount(coupon, plan)}
            />
          )}
        </Segment>
        <Segment>
          <UsageSection
            title={`Next invoice amount on ${timestampFormat(dueDate, 'mmm dd, yyyy')}`}
            priceDetail={
              isNextInvoiceAmountLoading ? (
                <Loader as="span" active inline size="small" />
              ) : (
                renderNextInvoiceAmount(plan, coupon, nextInvoiceAmount)
              )
            }
          />
        </Segment>
      </Segment.Group>
      <AllowPermission permission={constants.permissions.manageBillingInformation}>
        <div className="mt3">
          <Button onClick={() => onModalOpened('primary')}>Change plan</Button>
          <a className="ui primary ml1" href={cancellationLink} target="_blank" rel="noopener">
            Cancel subscription
          </a>
        </div>
      </AllowPermission>
    </React.Fragment>
  )
}

/**
 *
 * @param {object} props
 * @param {Plan} props.plan
 * @param {Subscription} props.subscription
 */
function Plan({ plan, subscription }) {
  if (subscription.status === 'active') {
    return (
      <span>
        <span>{plan.nickname}</span>
        <Label className="!bg-green-lighter !ml-2">Active</Label>
      </span>
    )
  }

  if (subscription.status === 'past_due') {
    return (
      <span>
        <span>{plan.nickname}</span>
        <Label className="!bg-orange-lighter !ml-2">Past due: feedback withheld</Label>
      </span>
    )
  }

  return <span>{plan?.nickname}</span>
}

/**
 *
 * @param {object} props
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.detail]
 * @param {React.ReactNode} [props.priceDetail]
 * @param {boolean} props.showPlanButton
 * @param {function} props.onModalOpened
 */
function UsageSection({ title, detail, priceDetail, onModalOpened, showPlanButton = false }) {
  return (
    <div className="first:mt-0 mt-4">
      <div className="flex flex-row">
        <FlexWrapper className="flex-1">
          <strong style={{ maxWidth: 300, minWidth: 100, flexShrink: 999 }}>{title}</strong>
          {detail && (
            <div>
              {[].concat(detail).map((item, index) => {
                return <p key={index}>{item}</p>
              })}
            </div>
          )}
          {priceDetail && (
            <strong className="ml-auto" style={{ minWidth: 150, flexGrow: 0 }}>
              {[].concat(priceDetail).map((item, index) => {
                return <p key={index}>{item}</p>
              })}
            </strong>
          )}
        </FlexWrapper>
        {showPlanButton && (
          <div className="flex justify-end items-center flex-shrink">
            <div>
              <Button onClick={() => onModalOpened('primary')} primary>
                Choose a plan
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const FlexWrapper = styled.div`
  --multiplier: calc(600px - 100%);
  --vertical-gap: 8px;
  display: flex;
  flex-wrap: wrap;
  margin-top: calc(var(--vertical-gap) * -1);

  > * {
    min-width: 200px;
    max-width: 100%;
    flex-grow: 1;
    flex-basis: calc(var(--multiplier) * 999);
    margin-top: var(--vertical-gap);

    &:empty {
      margin: 0;
    }
  }

  p {
    margin-bottom: var(---vertical-gap);
  }
`

/**
 *
 * @param {Subscription} subscription
 * @param {Plan} plan
 */
function renderPlanPricing(subscription, plan) {
  if (['active', 'past_due'].includes(subscription.status)) {
    return `${getPricing(priceAmount(plan), plan.currency)}/${priceInterval(plan)}`
  }

  return null
}

function getPricing(amount, currency) {
  const currencySymbolMap = {
    usd: '$',
  }

  if (currencySymbolMap[currency]) {
    return currencySymbolMap[currency] + formatNumber(amount)
  } else {
    return formatNumber(amount) + ' ' + currency.toUpperCase()
  }
}

/**
 *
 * @param {Coupon} coupon
 * @param {Plan} plan
 * @returns {string}
 */
function renderDiscount(coupon, plan) {
  if (coupon.amount_off) {
    return `(${getPricing(Number(coupon.amount_off), coupon.currency)})`
  }

  if (coupon.percent_off) {
    return `(${getPricing((Number(coupon.percent_off) / 100) * priceAmount(plan), plan.currency)})`
  }

  return null
}

/**
 *
 * @param {Coupon} coupon
 * @param {Plan} plan
 * @returns {number}
 */
function calculateDiscount(coupon, plan) {
  if (coupon.amount_off) {
    return Number(coupon.amount_off)
  }

  if (coupon.percent_off) {
    return (Number(coupon.percent_off) / 100) * priceAmount(plan)
  }

  return 0
}

/**
 *
 * @param {Plan} plan
 * @param {Coupon} coupon
 *
 */
function renderNextInvoiceAmount(plan, coupon, nextInvoiceAmount) {
  // Assume all unit is the same
  const amount = nextInvoiceAmount ?? calculateNextInvoiceAmount(plan, coupon)
  return getPricing(amount, plan.currency)
}

/**
 *
 * @param {Plan} plan
 * @param {Coupon} coupon
 *
 */
function calculateNextInvoiceAmount(plan, coupon) {
  // Assume all unit is the same. Floor at zero: matches how Stripe caps
  // invoices when a fixed deduction exceeds the plan price (PET-1986 AC11).
  const basePrice = priceAmount(plan)
  const discount = coupon ? calculateDiscount(coupon, plan) : 0

  return Math.max(0, basePrice - discount)
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/AccountPlanAndUsage/index.js